import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import Sheet from '../components/Sheet';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import { PRIMARY } from '../components/CaptureSheets';
import { DurationPicker } from '../components/CoachSheets';
import { copyText, qrDataUrl, shareLink, vnShortDate } from '../lib/coach';
import { useApp, useAppPath } from '../lib/AppContext';
import { NewBadge, useAnnouncementAnchor } from '../components/Announcements';

// Người chưa dùng app: the coach's clients without an account (F1 picker, F2 form, F4 group, F5 invite,
// F6 limit). Nothing here is ever sent to them.

const FIELD = 'input h-12 w-full rounded-[14px] border-line text-base';
const LABEL = 'text-sm font-semibold';
const TILE = 'flex flex-col items-center gap-1.5 text-xs font-semibold';
const ICON = 'grid size-12 place-items-center rounded-[14px]';
const pill = 'btn btn-sm h-9 rounded-full border border-primary bg-base-100 px-3 text-[#17804f]';

export const OfflineTag = () => <span className="rounded-full border border-line px-2 py-0.5 text-[10px] font-bold text-muted">Chưa dùng app</span>;

// "2/35"
export const countText = (limit) => (limit ? `${limit.count}/${limit.limit}` : '');

// The coach's offline clients with the limit; `reload` after any change.
export function useOfflineClients(enabled = true) {
  const { api } = useApp();
  const [data, setData] = useState(null);
  const reload = useCallback(() => api.get('/coach/offline_clients').then(setData).catch(() => {}), [api]);
  useEffect(() => { if (enabled) reload(); }, [enabled, reload]);
  return [data, reload];
}

// "Tập 7/8 buổi · lần cuối 3/11", "hẹn 6/11", or the pending invite.
function rowSubtitle(c) {
  const parts = [];
  if (c.past > 0) parts.push(`Tập ${c.trained}/${c.past} buổi`);
  if (c.last_at) parts.push(`lần cuối ${vnShortDate(c.last_at)}`);
  else if (c.next_at) parts.push(`hẹn ${vnShortDate(c.next_at)}`);
  if (parts.length === 0) parts.push('Chưa có buổi nào');
  return parts.join(' · ');
}

// F1 "Chọn học viên": students and people without the app, searchable, and "+ Thêm người chưa dùng app".
export function StudentPicker({ open, students, offline, limit, onClose, onPick, onAdd }) {
  const [query, setQuery] = useState('');
  const addAnchor = useAnnouncementAnchor('coach_offline_add');
  useEffect(() => { if (open) setQuery(''); }, [open]);
  const q = query.trim().toLowerCase();
  const match = (name) => !q || name.toLowerCase().includes(q);
  const row = 'flex min-h-14 w-full items-center gap-3 rounded-[14px] px-2 text-left hover:bg-base-200';
  return (
    <Sheet open={open} onClose={onClose} title="Chọn học viên">
      <div className="flex flex-col gap-3">
        <input className={FIELD} placeholder="Tìm tên…" aria-label="Tìm tên" value={query} onChange={(e) => setQuery(e.target.value)} />
        <ul className="flex max-h-[45dvh] flex-col overflow-y-auto">
          {students.filter((s) => match(s.name)).map((s) => (
            <li key={`s${s.client_id}`}>
              <button type="button" className={row} onClick={() => onPick({ kind: 'student', id: s.client_id, name: s.name, avatar_url: s.avatar_url })}>
                <Avatar name={s.name} url={s.avatar_url} size={36} />
                <span><span className="block font-semibold">{s.name}</span><span className="block text-xs text-muted">Dùng app</span></span>
              </button>
            </li>
          ))}
          {(offline || []).filter((c) => match(c.name)).map((c) => (
            <li key={`o${c.id}`}>
              <button type="button" className={row} onClick={() => onPick({ kind: 'offline', id: c.id, name: c.name })}>
                <Avatar name={c.name} dashed size={36} />
                <span className="min-w-0">
                  <span className="block font-semibold">{c.name}</span>
                  <span className="block truncate text-xs text-muted">Chưa dùng app{c.note ? ` · ${c.note}` : ''}</span>
                </span>
              </button>
            </li>
          ))}
          {q && ![...students, ...(offline || [])].some((p) => match(p.name)) && <li className="p-3 text-sm text-muted">Không tìm thấy “{query.trim()}”.</li>}
        </ul>
        <button ref={addAnchor} type="button" onClick={onAdd}
          className="flex min-h-12 items-center justify-center gap-2 rounded-[14px] border border-dashed border-primary bg-[#e3f1e9]/40 text-sm font-bold text-[#17804f]">
          <Icon name="plus" className="size-4" /> Thêm người chưa dùng app{limit ? ` · ${countText(limit)}` : ''}
          <NewBadge anchor="coach_offline_add" />
        </button>
        <p className="text-center text-xs text-muted">Khách chưa cài app? Chỉ cần tên để xếp lịch, mời dùng app sau.</p>
      </div>
    </Sheet>
  );
}

// F2 "Người chưa dùng app": add (then pick) one, or edit one (`client`). At the limit the server refuses and
// F6 opens instead (`onLimit`).
export function OfflineForm({ open, client, limit, onClose, onSaved, onLimit }) {
  const { api } = useApp();
  const [form, setForm] = useState({ name: '', phone: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!open) return;
    setForm({ name: client?.name || '', phone: client?.phone || '', note: client?.note || '' });
    setError(null);
  }, [open, client]);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const name = form.name.trim();

  const save = async (e) => {
    e.preventDefault();
    if (!name) return setError('Cần nhập tên');
    setBusy(true);
    setError(null);
    try {
      const body = { name, phone: form.phone.trim() || null, note: form.note.trim() || null };
      onSaved(client ? await api.patch(`/coach/offline_clients/${client.id}`, body) : await api.post('/coach/offline_clients', body));
    } catch (err) {
      if (err.code === 'offline_limit') onLimit(err.data?.limit);
      else setError(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const left = limit ? Math.max(limit.limit - limit.count, 0) : null;
  return (
    <Sheet open={open} onClose={onClose} title={client ? `Sửa ${client.name}` : 'Người chưa dùng app'}>
      <form className="flex flex-col gap-4" onSubmit={save} noValidate>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Tên</span>
          <input className={FIELD} maxLength={40} required autoComplete="off" value={form.name} onChange={set('name')} placeholder="VD: Anh Nam" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Số điện thoại <span className="font-normal text-muted">(không bắt buộc)</span></span>
          <input className={FIELD} type="tel" inputMode="tel" autoComplete="off" value={form.phone} onChange={set('phone')}
            placeholder="Dùng để gửi lời mời qua Zalo sau này" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Ghi chú <span className="font-normal text-muted">(không bắt buộc)</span></span>
          <input className={FIELD} maxLength={200} autoComplete="off" value={form.note} onChange={set('note')} placeholder="VD: Giảm mỡ, đau gối trái" />
        </label>
        {!client && limit && (
          <p className="flex gap-2 rounded-[14px] bg-[#e3f1e9]/60 p-3 text-xs text-[#1d5c3c]">
            <Icon name="check" className="mt-0.5 size-4 shrink-0" />
            <span>
              Không tốn chỗ. Còn <b>{left}/{limit.limit}</b> người chưa dùng app ({limit.free} miễn phí + {limit.per_seat} cho mỗi chỗ
              {limit.seats > 0 ? `, bạn có ${limit.seats} chỗ` : ''}). {name || 'Người này'} không nhận thông báo cho tới khi dùng app.
            </span>
          </p>
        )}
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        <button type="submit" className={PRIMARY} disabled={busy}>
          {busy && <span className="loading loading-spinner loading-sm" />} {client ? 'Lưu' : `Lưu và chọn ${name || 'người này'}`}
        </button>
      </form>
    </Sheet>
  );
}

// F6 "Đã đủ N người chưa dùng app": the tiers by seats, buying a seat, or freeing a place.
export function LimitSheet({ open, limit, onClose, onFree }) {
  const navigate = useNavigate();
  const path = useAppPath();
  if (!limit) return null;
  const next = limit.free + limit.per_seat * (limit.seats + 1);
  return (
    <Sheet open={open} onClose={onClose} title={`Đã đủ ${limit.limit} người chưa dùng app`}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">
          Bạn được {limit.free} người miễn phí. <b className="text-base-content">Mỗi chỗ = tặng Pro cho 1 học viên + theo dõi thêm {limit.per_seat} người.</b> Mời
          một người trong danh sách dùng app với Pro được tặng cũng giải phóng 1 suất.
        </p>
        <div className="grid grid-cols-4 gap-2">
          {limit.tiers.map((n, seats) => (
            <div key={seats} className={`flex flex-col items-center rounded-[14px] border-2 py-2 ${seats === limit.seats ? 'border-primary bg-[#e3f1e9]/50' : 'border-line'}`}>
              <span className="text-[11px] text-muted">{seats} chỗ</span>
              <span className="tabular text-lg font-extrabold">{n}</span>
              <span className="text-[10px] text-muted">người</span>
            </div>
          ))}
        </div>
        <button type="button" className={`${PRIMARY} flex-col gap-0`} onClick={() => { onClose(); navigate(path(limit.seats === 0 ? '/coach/seats?seats=1' : '/coach/seats')); }}>
          <span>{limit.seats === 0 ? 'Mua chỗ đầu tiên' : 'Thêm 1 chỗ'}</span>
          <span className="text-[11px] font-medium opacity-80">tặng Pro 1 học viên + lên {next} người</span>
        </button>
        <div className="flex flex-col gap-1 text-sm">
          <p className="text-xs text-muted">Hoặc giải phóng một suất:</p>
          <button type="button" className="min-h-11 text-left font-semibold text-brand-text" onClick={() => onFree('invite')}>Mời một người dùng app</button>
          <button type="button" className="min-h-11 text-left font-semibold text-brand-text" onClick={() => onFree('delete')}>Xoá người không còn tập</button>
        </div>
      </div>
    </Sheet>
  );
}

// F5 "Mời <tên> dùng app": a free connection (no seat) or a Pro gift (one seat), by Zalo (to their number when
// the coach saved one), Messenger, QR or a copied link. The invite is made on the first channel tapped.
export function OfflineInviteSheet({ client, plan, onClose, onSent }) {
  const { api } = useApp();
  const [choice, setChoice] = useState('free');
  const [duration, setDuration] = useState('1_month');
  const [invite, setInvite] = useState(null); // { key, url, ... }
  const [qr, setQr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);
  const open = !!client;
  useEffect(() => {
    if (!open) return;
    setChoice('free');
    setDuration('1_month');
    setInvite(null);
    setQr(null);
    setNote(null);
    setError(null);
  }, [open, client?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const seatsFree = plan?.active ? plan.seats_free : 0;
  const key = choice === 'pro' ? `pro:${duration}` : 'free';
  const name = client?.name || '';

  // One invite per choice: changing Free/Pro or the duration makes a new one (the old link stops working).
  const ensure = async () => {
    if (invite?.key === key) return invite;
    setBusy(true);
    setError(null);
    try {
      const made = await api.post(`/coach/offline_clients/${client.id}/invite`, { pro: choice === 'pro', duration: choice === 'pro' ? duration : undefined });
      const next = { ...made, key };
      setInvite(next);
      onSent?.();
      return next;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  };
  const text = choice === 'pro'
    ? `Coach mời bạn dùng Macro Mate, tặng kèm Pro. Lịch tập của bạn đã có sẵn trong app:`
    : 'Coach mời bạn dùng Macro Mate để xem lịch tập, được nhắc trước giờ tập:';

  const zalo = async () => {
    const made = await ensure();
    if (!made) return;
    if (client.phone) {
      await copyText(`${text} ${made.url}`);
      setNote('Đã chép lời mời, dán vào Zalo');
      window.open(`https://zalo.me/${client.phone}`, '_blank', 'noopener');
    } else if ((await shareLink({ title: 'Lời mời Macro Mate', text, url: made.url })) === 'copied') setNote('Đã sao chép link');
  };
  const messenger = async () => {
    const made = await ensure();
    if (made && (await shareLink({ title: 'Lời mời Macro Mate', text, url: made.url })) === 'copied') setNote('Đã sao chép link');
  };
  const showQr = async () => {
    const made = await ensure();
    if (made) setQr(await qrDataUrl(made.url));
  };
  const copy = async () => {
    const made = await ensure();
    if (made) { await copyText(made.url); setNote('Đã sao chép link'); }
  };

  const radio = (on) => `flex w-full items-start gap-3 rounded-[16px] border-2 p-3 text-left ${on ? 'border-primary bg-[#e3f1e9]/50' : 'border-line'}`;
  const dot = (on) => (
    <span className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border-2 ${on ? 'border-primary' : 'border-line'}`}>
      {on && <span className="size-2.5 rounded-full bg-primary" />}
    </span>
  );
  return (
    <Sheet open={open} onClose={onClose} label={`Mời ${name} dùng app`}>
      {client && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Avatar name={name} dashed size={44} />
            <div>
              <p className="text-lg font-bold">Mời {name} dùng app</p>
              <p className="text-xs text-muted">{client.past} buổi đã ghi · {client.upcoming} lịch hẹn sắp tới</p>
            </div>
          </div>
          <div role="radiogroup" aria-label="Cách mời" className="flex flex-col gap-2">
            <button type="button" role="radio" aria-checked={choice === 'free'} className={radio(choice === 'free')} onClick={() => setChoice('free')}>
              {dot(choice === 'free')}
              <span>
                <span className="flex items-center gap-1.5 font-bold">Kết nối miễn phí <span className="rounded-full bg-[#e3f1e9] px-1.5 text-[10px] text-[#17804f]">MIỄN PHÍ</span></span>
                <span className="block text-xs text-muted">{name} dùng gói Free hoặc tự mua Pro. Không tốn chỗ.</span>
              </span>
            </button>
            <button type="button" role="radio" aria-checked={choice === 'pro'} disabled={seatsFree <= 0} className={`${radio(choice === 'pro')} disabled:opacity-50`}
              onClick={() => setChoice('pro')}>
              {dot(choice === 'pro')}
              <span>
                <span className="block font-bold">Tặng Pro</span>
                <span className="block text-xs text-muted">{seatsFree > 0 ? `Dùng 1 chỗ · còn ${seatsFree} chỗ trống.` : 'Hết chỗ trống để tặng Pro.'}</span>
              </span>
            </button>
          </div>
          {choice === 'pro' && <DurationPicker value={duration} onChange={setDuration} plan={plan} />}
          {qr ? (
            <div className="flex flex-col items-center gap-2">
              <img src={qr} alt={`Mã QR mời ${name}`} className="size-56 rounded-[16px] border border-line" />
              <button type="button" className="btn btn-ghost h-11 rounded-2xl" onClick={() => setQr(null)}>Quay lại</button>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              <button type="button" className={TILE} disabled={busy} onClick={zalo}>
                <span className={`${ICON} bg-[#e5f0ff] text-[13px] font-extrabold text-[#0068ff]`}>Zalo</span>Zalo
              </button>
              <button type="button" className={TILE} disabled={busy} onClick={messenger}>
                <span className={`${ICON} bg-[#efe8ff] text-[#7b3fe4]`}><Icon name="send" className="size-6" /></span>Messenger
              </button>
              <button type="button" className={TILE} disabled={busy} onClick={showQr}>
                <span className={`${ICON} bg-[#17201b] text-white`}><Icon name="qr" className="size-6" /></span>Mã QR
              </button>
              <button type="button" className={TILE} disabled={busy} onClick={copy}>
                <span className={`${ICON} bg-base-200`}><Icon name="copy" className="size-6" /></span>Sao chép
              </button>
            </div>
          )}
          {busy && <p className="flex items-center justify-center gap-2 text-sm text-muted"><span className="loading loading-spinner loading-xs" /> Đang tạo link…</p>}
          {note && <p role="status" className="text-center text-sm font-semibold text-brand-text">{note}</p>}
          {error && <p role="alert" className="text-center text-sm font-medium text-error">{error}</p>}
          <p className="rounded-[14px] bg-base-200 p-3 text-xs text-muted">
            Khi {name} nhận lời, lịch hẹn sắp tới và lịch sử buổi tập chuyển sang tài khoản của {name}. Từ đó {name} nhận nhắc lịch, báo nghỉ,
            xin đổi giờ được. Link dùng 1 lần, hết hạn sau 7 ngày.
          </p>
        </div>
      )}
    </Sheet>
  );
}

// "Xoá <tên>?": future sessions are cancelled, the profile and its attendance deleted.
function DeleteSheet({ client, busy, onClose, onConfirm }) {
  return (
    <Sheet open={!!client} onClose={onClose} title={`Xoá ${client?.name || ''}?`}>
      {client && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            Huỷ {client.upcoming} buổi hẹn sắp tới, xoá hồ sơ và lịch sử chuyên cần ({client.past} buổi) của {client.name}. Không hoàn tác được.
          </p>
          <button type="button" className="btn h-12 rounded-[16px] border-0 bg-[#c2410c] font-bold text-white" disabled={busy} onClick={onConfirm}>
            {busy && <span className="loading loading-spinner loading-sm" />} Xoá {client.name}
          </button>
          <button type="button" className="btn btn-ghost h-11" onClick={onClose}>Giữ lại</button>
        </div>
      )}
    </Sheet>
  );
}

// F4 group on Học viên: "Chưa dùng app · 2/35", each person's attendance and "Mời dùng app"; a row opens
// Sửa / Thêm lịch tập / Mời dùng app / Xoá. `focus`: F6 sent the coach here to free a place.
export function OfflineGroup({ plan, onChanged, say, focus }) {
  const { api } = useApp();
  const navigate = useNavigate();
  const path = useAppPath();
  const [data, reload] = useOfflineClients();
  const [options, setOptions] = useState(null);
  const [editing, setEditing] = useState(null); // { client } (null client: new)
  const [inviting, setInviting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [limitOpen, setLimitOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const clients = data?.offline_clients || [];
  const limit = data?.limit;
  const changed = () => { reload(); onChanged?.(); };

  const act = (action) => {
    const client = options;
    setOptions(null);
    if (action === 'edit') setEditing({ client });
    if (action === 'invite') setInviting(client);
    if (action === 'delete') setDeleting(client);
    if (action === 'schedule') navigate(path(`/coach/plan?part=schedule&add_offline=${client.id}`));
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/coach/offline_clients/${deleting.id}`);
      say(`Đã xoá ${deleting.name}`);
      setDeleting(null);
      changed();
    } catch (e) {
      say(e.message);
    } finally {
      setBusy(false);
    }
  };
  const add = () => (limit && limit.count >= limit.limit ? setLimitOpen(true) : setEditing({ client: null }));

  const title = useMemo(() => `Chưa dùng app${limit ? ` · ${countText(limit)}` : ''}`, [limit]);
  if (!data) return null;
  return (
    <section id="offline" className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between px-1 pt-3">
        <h2 className="text-xs font-bold uppercase tracking-wide text-muted">{title}</h2>
        <span className="text-[11px] text-muted">{limit.free} miễn phí + {limit.per_seat} mỗi chỗ</span>
      </div>
      {focus && <p className="px-1 text-xs font-semibold text-[#c2410c]">Mời một người dùng app, hoặc xoá người không còn tập để thêm người mới.</p>}
      <ul className="divide-y divide-line rounded-[20px] bg-base-100 px-4">
        {clients.map((c) => (
          <li key={c.id} className="flex min-h-16 items-center gap-3 py-2">
            <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => setOptions(c)} aria-label={`Tuỳ chọn cho ${c.name}`}>
              <Avatar name={c.name} dashed size={40} />
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="truncate font-semibold">{c.name}</span>
                  {c.invite?.status === 'pending' && <span className="rounded-full bg-[#fdf3d7] px-2 py-0.5 text-[11px] font-bold text-[#8a6100]">Chờ nhận</span>}
                </span>
                <span className="block truncate text-xs text-muted">{rowSubtitle(c)}</span>
              </span>
            </button>
            <button type="button" className={pill} onClick={() => setInviting(c)}>{c.invite?.status === 'pending' ? 'Gửi lại' : 'Mời dùng app'}</button>
          </li>
        ))}
        <li>
          <button type="button" className="flex min-h-14 w-full items-center gap-2 text-sm font-bold text-[#17804f]" onClick={add}>
            <Icon name="plus" className="size-4" /> Thêm người chưa dùng app
          </button>
        </li>
      </ul>
      {clients.length === 0 && <p className="px-1 text-xs text-muted">Khách chưa cài app? Chỉ cần tên để xếp lịch, mời dùng app sau.</p>}

      <Sheet open={!!options} onClose={() => setOptions(null)} title={options?.name}>
        {options && (
          <div className="flex flex-col gap-2">
            {(options.phone || options.note) && <p className="pb-1 text-sm text-muted">{[options.phone, options.note].filter(Boolean).join(' · ')}</p>}
            {[['schedule', 'calendar', 'Thêm lịch tập'], ['invite', 'send', 'Mời dùng app'], ['edit', 'pencil', 'Sửa thông tin']].map(([id, icon, label]) => (
              <button key={id} type="button" className="flex min-h-14 items-center gap-3 rounded-[16px] border border-line px-4 text-left font-semibold" onClick={() => act(id)}>
                <Icon name={icon} className="size-5" /> {label}
              </button>
            ))}
            <button type="button" className="flex min-h-14 items-center gap-3 rounded-[16px] border border-line px-4 text-left font-semibold text-error" onClick={() => act('delete')}>
              <Icon name="trash" className="size-5" /> Xoá
            </button>
          </div>
        )}
      </Sheet>
      <OfflineForm open={!!editing} client={editing?.client} limit={limit} onClose={() => setEditing(null)}
        onSaved={(saved) => { setEditing(null); say(editing?.client ? 'Đã lưu' : `Đã thêm ${saved.name}`); changed(); }}
        onLimit={() => { setEditing(null); setLimitOpen(true); reload(); }} />
      <OfflineInviteSheet client={inviting} plan={plan} onClose={() => { setInviting(null); changed(); }} onSent={reload} />
      <DeleteSheet client={deleting} busy={busy} onClose={() => setDeleting(null)} onConfirm={remove} />
      <LimitSheet open={limitOpen} limit={limit} onClose={() => setLimitOpen(false)}
        onFree={() => { setLimitOpen(false); document.getElementById('offline')?.scrollIntoView({ behavior: 'smooth' }); }} />
    </section>
  );
}
